import { Banner } from "@/components/flexkit/Banner";
import { cn } from "@/components/podkit/lib/cn";
import IconLightBulb from "central-icons/IconLightBulb";
import { StatusDot } from "@/components/flexkit/status-dot/StatusDot";
import { useOrganization } from "@/queries/organization-queries";
import { useGetPreferenceByKey, useSetPreference } from "@/queries/user-preferences-queries";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useExitTransition } from "@/hooks/use-exit-transition";
import { useCallback, useState, type FC } from "react";

const DEVCONTAINER_EDUCATION_DISMISSED_KEY = "devcontainer_education_banner_dismissed";

function useDevcontainerEducation() {
    const { data: organization } = useOrganization();
    const { data: preference, isPending } = useGetPreferenceByKey(DEVCONTAINER_EDUCATION_DISMISSED_KEY);
    const { mutate: setPreference } = useSetPreference();

    const [localDismissed, setLocalDismissed] = useState(false);

    const isEnterprise = organization?.tier === OrganizationTier.ENTERPRISE;
    const isDismissed = localDismissed || preference?.value === "true";
    const visible = !isEnterprise && !isDismissed && !isPending;

    const dismiss = useCallback(() => {
        setLocalDismissed(true);
        setPreference({ key: DEVCONTAINER_EDUCATION_DISMISSED_KEY, value: "true" });
    }, [setPreference]);

    return { visible, dismiss, isPending };
}

export const DevcontainerEducationDot: FC = () => {
    const { visible } = useDevcontainerEducation();
    const { shouldRender, exiting, onTransitionEnd } = useExitTransition(visible);

    if (!shouldRender) return null;

    return (
        <div
            className={cn("transition-transform duration-200 ease-in-out", exiting ? "scale-0" : "scale-100")}
            onTransitionEnd={onTransitionEnd}
        >
            <StatusDot
                size="lg"
                color="brand"
                animation="ping"
                tooltip="Dev container configuration"
                className="size-4"
            />
        </div>
    );
};

export const DevcontainerEducationBanner: FC<{ className?: string }> = ({ className }) => {
    const { visible, dismiss } = useDevcontainerEducation();
    const { shouldRender, exiting, onTransitionEnd } = useExitTransition(visible);

    if (!shouldRender) return null;

    return (
        <div
            className={cn(
                "grid transition-[grid-template-rows] duration-200 ease-in-out",
                exiting ? "grid-rows-[0fr]" : "grid-rows-[1fr]",
            )}
            onTransitionEnd={onTransitionEnd}
        >
            <div className="overflow-hidden">
                {/* Targets Banner's internal overflow-hidden div to prevent the bulb bounce
                    animation from clipping. Fragile: if Banner markup changes, revisit. */}
                <Banner
                    variant="announcement"
                    className={cn("overflow-visible [&>div:first-child]:overflow-visible", className)}
                    LeadingIcon={<IconLightBulb size={16} className="animate-squash-bounce text-content-brand" />}
                    text={
                        <span className="select-none text-sm font-normal text-content-brand">
                            A Dev Container defines everything your project needs to run - language runtimes, databases,
                            tools, and configuration.
                        </span>
                    }
                    link={
                        <a
                            href="https://ona.com/docs/ona/configuration/devcontainer/getting-started#why-dev-containers-matter"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-content-brand underline"
                        >
                            Learn more
                        </a>
                    }
                    action={{
                        text: "Got it",
                        onClick: dismiss,
                        size: "xs",
                        variant: "outline",
                        className: "text-content-brand border-content-brand hover:text-content-brand",
                        "data-tracking-id": "devcontainer-education-banner-dismiss",
                    }}
                />
            </div>
        </div>
    );
};
